import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Alert, Button, Loader, Modal, Select, Table } from '@itds/elements/index.js';
import { Colors, FontSizes } from '@itds/Theme';
import { STATES } from '@itds/Constants';

import NewDrawer from './New';
import BulkEditDrawer from './BulkEdit';

import Api from '@carrier/utils/Api';

const List = ({ version }) => {
  const [quote_rates, setQuoteRates] = useState([]);
  const [quote_rate, setQuoteRate] = useState(null);
  const [products, setProducts] = useState([]);
  const [bands, setBands] = useState([]);
  const [modal_factors, setModalFactors] = useState([]);
  const [riders, setRiders] = useState([]);
  const [health_categories, setHealthCategories] = useState([]);
  const [table_rates, setTableRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected_rates, setSelectedRates] = useState([]);
  const [bulk_edit_rates, setBulkEditRates] = useState([]);
  const [filters, setFilters] = useState({
    product_id: '',
    band_id: '',
    health_category_id: '',
    state: '',
    gender: ''
  });
  const [filtered_rates, setFilteredRates] = useState([]);
  const [show_bulk_edit, toggleBulkEdit] = useState(false);
  const [show_bulk_delete, toggleBulkDelete] = useState(false);
  const [show_bulk_copy, toggleBulkCopy] = useState(false);
  const [error_message, setErrorMessage] = useState(null);
  const [success_message, setSuccessMessage] = useState(null);

  useEffect(() => {
    const request_params = { carrier_id: version.carrier_id, data_version_id: version.id };

    Promise.all([
      Api.loadQuoteRates(request_params),
      Api.loadProducts(request_params),
      Api.loadBands(request_params),
      Api.loadModalFactors(request_params),
      Api.loadRiders(request_params),
      Api.loadHealthCategories(request_params),
      Api.loadTableRates(request_params)
    ]).then(results => {
      setQuoteRates(results[0].data);
      setFilteredRates(results[0].data);
      setProducts(results[1].data);
      setBands(results[2].data);
      setModalFactors(results[3].data);
      setRiders(results[4].data);
      setHealthCategories(results[5].data);
      setTableRates(results[6].data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const new_rates = quote_rates.filter(rate => {
      const checks = [];

      if (filters.product_id) {
        checks.push(`${rate.product_id}` === `${filters.product_id}`);
      }

      if (filters.band_id) {
        checks.push(`${rate.band_id}` === `${filters.band_id}`);
      }

      if (filters.health_category_id) {
        checks.push(`${rate.health_category_id}` === `${filters.health_category_id}`);
      }

      if (filters.state) {
        let val = rate[`state_${filters.state}`];

        if (val === null) {
          val = false;
        }

        checks.push(val);
      }

      if (filters.gender) {
        checks.push(rate.gender === filters.gender);
      }

      return !checks.includes(false);
    });

    setFilteredRates(new_rates);
  }, [quote_rates, filters]);

  const loadQuoteRates = action => {
    setLoading(true);

    setTimeout(() => {
      setSuccessMessage(null);
      setErrorMessage(null);
    }, 2000);

    Api.loadQuoteRates({ carrier_id: version.carrier_id, data_version_id: version.id })
      .then(({ data }) => {
        setQuoteRates(data);
        setFilteredRates(data);
        setLoading(false);
        if (action === 'add') {
          setSuccessMessage('Quote Rate Successfully Added');
        } else if (action === 'delete') {
          setSuccessMessage('Quote Rate  Successfully Deleted');
        }
      })
      .catch(() => {
        if (action === 'add') {
          setErrorMessage('Unable to add Quote Rate at this time');
        } else if (action === 'delete') {
          setErrorMessage('Unable to delete Quote Rate at this time');
        }
      });
  };

  const handleSelectAll = e => {
    e.stopPropagation();

    if (filtered_rates.length === selected_rates.length) {
      setSelectedRates([]);
      setBulkEditRates([]);
    } else {
      setSelectedRates(filtered_rates.map(fr => fr.id));
      setBulkEditRates(filtered_rates.map(fr => fr));
    }
  };

  const handleRateSelectChange = (rate, e) => {
    e.stopPropagation();

    // get selected IDs to use in this file for selecting/copying/deleting/etc.
    const selected_rate_ids = selected_rates.filter(id => id !== rate.id);

    if (!selected_rates.includes(rate.id)) {
      selected_rate_ids.push(rate.id);
    }
    setSelectedRates(selected_rate_ids);

    // get selected rate objects to send to BulkEdit
    const selected_rate_objects = quote_rates.filter(qr => selected_rate_ids.includes(qr.id));

    setBulkEditRates(selected_rate_objects);
  };

  const handleFilterChange = (name, e) => {
    setFilters({ ...filters, [name]: e.target.value });
  };

  const handleBulkDelete = () => {
    setLoading(true);
    toggleBulkDelete(false);

    Promise.all(selected_rates.map(id => Api.deleteQuoteRate(id))).then(() => {
      setSelectedRates([]);
      loadQuoteRates('delete');
      setBulkEditRates([]);
      loadQuoteRates();
    });
  };

  const handleDelete = (rate, e) => {
    e.stopPropagation();

    setSelectedRates([rate.id]);
    toggleBulkDelete(true);
  };

  const handleSave = () => {
    setQuoteRate(null);
    setSelectedRates([]);
    loadQuoteRates('add');
    setBulkEditRates([]);
    loadQuoteRates();
  };

  const handleQuoteRateClick = rate => {
    openEditNewTab(rate);
    // Api.loadQuoteRate(rate.id).then(({ data }) => {
    //   setQuoteRate(data);
    // });
  };

  const handleBulkCopy = () => {
    setLoading(true);
    toggleBulkCopy(false);

    Promise.all(
      selected_rates.map(rate_id => {
        return Api.loadQuoteRate(rate_id);
      })
    ).then(results => {
      Promise.all(
        results.map(({ data }) => {
          const { id, created_at, updated_at, age_rates, ...payload } = data;

          payload.age_rates_attributes = [];

          age_rates.forEach(age_rate => {
            const { id, quote_rate_id, created_at, updated_at, ...age_rate_payload } = age_rate;

            payload.age_rates_attributes.push(age_rate_payload);
          });

          return Api.createQuoteRate(payload);
        })
      ).then(() => {
        loadQuoteRates();
      });
    });
  };

  const handleCopy = (rate, e) => {
    const { id, created_at, updated_at, ...payload } = rate;

    e.stopPropagation();

    payload.age_rates_attributes = [];

    setLoading(true);

    Api.loadQuoteRate(rate.id).then(({ data }) => {
      data.age_rates.forEach(age_rate => {
        const { id, quote_rate_id, created_at, updated_at, ...age_rate_payload } = age_rate;

        payload.age_rates_attributes.push(age_rate_payload);
      });

      Api.createQuoteRate(payload).then(() => {
        loadQuoteRates();
      });
    });
  };

  const bulkEditClose = () => {
    setBulkEditRates([]);
    setSelectedRates([]);
    toggleBulkEdit(false);
  };
  const openEditNewTab = rate => {
    window.open(
      `/#/carriers/${version.carrier_id}/life_quotes/${version.id}/quote_rates/${rate.id}`
    );
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      {bands.length > 0 &&
      health_categories.length > 0 &&
      modal_factors.length > 0 &&
      products.length > 0 ? (
        <NewDrawer
          bands={bands}
          healthCategories={health_categories}
          modalFactors={modal_factors}
          onClose={setQuoteRate.bind(null, null)}
          onSave={handleSave}
          products={products}
          quoteRate={quote_rate}
          riders={riders}
          show={!!quote_rate}
          tableRates={table_rates}
        />
      ) : null}

      <BulkEditDrawer
        bands={bands}
        healthCategories={health_categories}
        modalFactors={modal_factors}
        onClose={bulkEditClose}
        onSave={handleSave}
        products={products}
        rates={bulk_edit_rates}
        riders={riders}
        show={show_bulk_edit}
        tableRates={table_rates}
      />

      {show_bulk_delete ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              onClick: handleBulkDelete,
              children: 'Delete',
              style: { marginLeft: 'auto' }
            }
          ]}
          onClose={toggleBulkDelete.bind(null, null)}
          title='Are you sure?'
        >
          <div
            style={{
              padding: 30,
              lineHeight: '30px',
              fontSize: FontSizes.LARGE,
              textAlign: 'center'
            }}
          >
            Be careful...you are about to delete {selected_rates.length} quote rates. This will
            remove them completely from the platform. Make sure you know what you are doing before
            completing this action.
          </div>
        </Modal>
      ) : null}

      {show_bulk_copy ? (
        <Modal
          buttons={[
            {
              onClick: handleBulkCopy,
              children: 'Start Copying',
              style: { marginLeft: 'auto' }
            }
          ]}
          onClose={toggleBulkCopy.bind(null, null)}
          title='Are you sure?'
        >
          <div
            style={{
              padding: 30,
              lineHeight: '30px',
              fontSize: FontSizes.LARGE,
              textAlign: 'center'
            }}
          >
            Be careful...you are about to copy {selected_rates.length} quote rates. This will create
            an identical copy of each rate and assign them to this life quotes version. Make sure
            you know what you are doing before completing this action.
          </div>
        </Modal>
      ) : null}

      <div style={{ display: 'flex', marginBottom: 30 }}>
        <Select
          onChange={handleFilterChange.bind(null, 'product_id')}
          options={[{ value: '', label: 'All Products' }].concat(
            products.map(p => ({ value: p.id, label: p.product_name }))
          )}
          style={{ marginRight: 5, width: 120 }}
          value={filters.product_id}
        />
        <Select
          onChange={handleFilterChange.bind(null, 'band_id')}
          options={[{ value: '', label: 'All Bands' }].concat(
            bands.map(b => ({ value: b.id, label: b.band_name }))
          )}
          style={{ marginRight: 5, width: 120 }}
          value={filters.band_id}
        />
        <Select
          onChange={handleFilterChange.bind(null, 'health_category_id')}
          options={[{ value: '', label: 'All Health Categories' }].concat(
            health_categories.map(hc => ({ value: hc.id, label: hc.carrier_health_category }))
          )}
          style={{ marginRight: 5, width: 150 }}
          value={filters.health_category_id}
        />
        <Select
          onChange={handleFilterChange.bind(null, 'gender')}
          options={[
            { value: '', label: 'All Genders' },
            { value: 'Unisex', label: 'Unisex' },
            { value: 'Male' },
            { value: 'Female' }
          ]}
          style={{ marginRight: 5 }}
          value={filters.gender}
        />
        <Select
          onChange={handleFilterChange.bind(null, 'state')}
          options={[{ value: '', label: 'All States' }].concat(STATES)}
          style={{ width: 120 }}
          value={filters.state}
        />

        <div style={{ marginLeft: 'auto' }}>
          <Button
            color='RED'
            disabled={!selected_rates.length}
            inline={true}
            onClick={toggleBulkDelete.bind(null, true)}
            style={{ marginRight: 5 }}
          >
            Delete
          </Button>
          <Button
            disabled={!selected_rates.length}
            inline={true}
            onClick={toggleBulkCopy.bind(null, true)}
            style={{ marginRight: 5 }}
          >
            Copy
          </Button>
          <Button
            disabled={!selected_rates.length}
            inline={true}
            onClick={toggleBulkEdit.bind(null, true)}
            style={{ marginRight: 5 }}
          >
            Edit
          </Button>
          <Button
            disabled={
              bands.length === 0 ||
              health_categories.length === 0 ||
              modal_factors.length === 0 ||
              products.length === 0
            }
            inline={true}
            onClick={setQuoteRate.bind(null, {
              carrier_id: version.carrier_id,
              data_version_id: version.id
            })}
          >
            New
          </Button>
        </div>
      </div>

      <Table
        columns={[
          {
            id: 'select',
            label: (
              <i
                className={`mdi ${
                  filtered_rates.length === selected_rates.length
                    ? 'mdi-checkbox-marked'
                    : 'mdi-checkbox-blank-outline'
                }`}
                onClick={handleSelectAll}
                style={{
                  fontSize: 18,
                  cursor: 'pointer',
                  color: filtered_rates.length === selected_rates.length ? Colors.GREEN.hex : '#777'
                }}
              />
            ),
            render: rate => {
              const selected = selected_rates.includes(rate.id);

              return (
                <i
                  className={`mdi ${
                    selected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
                  }`}
                  onClick={handleRateSelectChange.bind(null, rate)}
                  style={{
                    fontSize: 18,
                    cursor: 'pointer',
                    color: selected ? Colors.GREEN.hex : '#777'
                  }}
                />
              );
            }
          },
          {
            id: 'product_id',
            label: 'Product',
            is_sortable: true,
            render: rate => {
              const product = products.find(p => p.id === rate.product_id);

              return product ? product.product_name : null;
            }
          },
          {
            id: 'band_id',
            label: 'Band',
            is_sortable: true,
            render: rate => {
              const band = bands.find(b => b.id === rate.band_id);

              return band ? band.band_name : null;
            }
          },
          {
            id: 'health_category_id',
            label: 'Health',
            is_sortable: true,
            render: rate => {
              const health_category = health_categories.find(
                hc => hc.id === rate.health_category_id
              );

              return health_category ? health_category.carrier_health_category : null;
            }
          },
          {
            id: 'gender',
            label: 'Gender',
            is_sortable: true,
            render: rate => (rate.gender === 'Both' ? 'Unisex' : rate.gender)
          },
          {
            id: 'tobacco',
            label: 'Tobacco',
            is_sortable: true,
            render: rate =>
              rate.tobacco ? (
                <i
                  className='mdi mdi-check-bold'
                  style={{ fontSize: 20, color: Colors.GREEN.hex }}
                />
              ) : null,
            styles: { textAlign: 'center' }
          },
          {
            id: 'table_rate_id',
            label: 'Table Rate',
            is_sortable: true,
            render: rate =>
              rate.table_rate_id ? (
                <i
                  className='mdi mdi-check-bold'
                  style={{ fontSize: 20, color: Colors.GREEN.hex }}
                />
              ) : null,
            styles: { textAlign: 'center' }
          },
          {
            id: 'riders',
            label: 'Riders',
            is_sortable: true,
            render: rate => {
              return (rate.adb_rider_ids && rate.adb_rider_ids.length) ||
                (rate.wop_rider_ids && rate.wop_rider_ids.length) ||
                (rate.child_rider_ids && rate.child_rider_ids.length) ||
                (rate.child_wop_rider_ids && rate.child_wop_rider_ids.length) ? (
                <i
                  className='mdi mdi-check-bold'
                  style={{ fontSize: 20, color: Colors.GREEN.hex }}
                />
              ) : null;
            },
            styles: { textAlign: 'center' }
          },
          {
            id: 'states',
            label: 'States',
            is_sortable: true,
            render: rate => {
              const selected_states = STATES.filter(s => rate[`state_${s.value}`]).map(
                s => s.value
              );

              return selected_states.length > 1
                ? `${selected_states.length} Selected`
                : selected_states[0];
            },
            width: 120
          },
          {
            id: 'updated_at',
            label: 'Updated',
            is_sortable: true,
            render: rate => format(new Date(rate.updated_at), 'MM/dd/yyyy'),
            width: 100
          },
          {
            id: 'actions',
            label: '',
            render: rate => {
              return (
                <>
                  <Button inline={true} onClick={handleCopy.bind(null, rate)} small={true}>
                    Copy
                  </Button>
                  <Button
                    color='RED'
                    inline={true}
                    onClick={handleDelete.bind(null, rate)}
                    small={true}
                    style={{ marginLeft: 5 }}
                  >
                    Delete
                  </Button>
                </>
              );
            },
            styles: { textAlign: 'right' },
            width: 150
          }
        ]}
        data={filtered_rates}
        defaultSortColumn={{
          id: 'product_id',
          label: 'Product',
          is_sortable: true,
          render: rate => {
            const product = products.find(p => p.id === rate.product_id);

            return product ? product.product_name : null;
          }
        }}
        onRowClick={handleQuoteRateClick}
        perPage={20}
        placeholder='No quote rates found'
      />
    </>
  );
};

List.propTypes = {
  version: PropTypes.object
};

export default List;
